import api from '../lib/api';

export const analyzeService = {
  /** POST /api/analyze/score (with determinism envelope) */
  async score(text, sessionMode = null, context = {}) {
    const payload = { text, session_mode: sessionMode };
    if (context.userPrompt)  payload.user_prompt  = context.userPrompt;
    if (context.domain)      payload.domain       = context.domain;
    if (context.audience)    payload.audience     = context.audience;
    if (context.sensitivity) payload.sensitivity  = context.sensitivity;
    const response = await api.post('/analyze/score', payload);
    return response.data;
  },

  /** POST /api/analyze/optimize/:id */
  async optimize(runId) {
    const response = await api.post(`/analyze/optimize/${runId}`);
    return response.data;
  },

  /**
   * SSE-based optimize with progress streaming.
   * Falls back to regular POST if SSE fails.
   * @param {string} runId
   * @param {function} onProgress - called with { stage, percent, title, desc }
   * @returns {Promise<object>} final result
   */
  optimizeStream(runId, onProgress) {
    return new Promise((resolve, reject) => {
      let settled = false;
      let pollInterval = null;

      function stopPolling() {
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      }

      // Step 1: Start optimization (returns immediately)
      fetch(`/api/optimize/start/${runId}`, {
        method: 'POST',
        credentials: 'include',
      })
        .then(response => {
          if (!response.ok) {
            return response.text().then(text => {
              let msg = `HTTP ${response.status}`;
              try { const err = JSON.parse(text); msg = err?.detail?.error || err?.detail || msg; } catch {}
              throw new Error(msg);
            });
          }
          return response.json();
        })
        .then(data => {
          if (!data) return;
          // Step 2: Poll for progress every 600ms
          pollInterval = setInterval(async () => {
            try {
              const resp = await fetch(`/api/optimize/progress/${runId}`, {
                credentials: 'include',
              });
              if (!resp.ok) return;
              const data = await resp.json();

              // Update progress UI
              if (onProgress && data.stage > 0) {
                onProgress({
                  stage: data.stage,
                  percent: data.percent,
                  title: data.title,
                  desc: data.desc,
                });
              }

              // Check if done
              if (data.done) {
                stopPolling();
                if (!settled) {
                  settled = true;
                  // Error envelope: backend records {error, user_title, user_message}
                  // in `result` when the background task fails. Detect that and
                  // reject with a friendly message rather than a generic dump.
                  if (data.result && data.result.error) {
                    const friendly = data.result.user_message || data.result.user_title || data.desc || 'Optimization failed';
                    const err = new Error(friendly);
                    err.userTitle = data.result.user_title || 'Optimization failed';
                    err.userMessage = data.result.user_message || friendly;
                    reject(err);
                  } else if (data.result) {
                    resolve(data.result);
                  } else if (data.title === 'Error' || data.title === 'Optimization failed' || data.title === 'AI capacity reached') {
                    const err = new Error(data.desc || data.title);
                    err.userTitle = data.title;
                    err.userMessage = data.desc || data.title;
                    reject(err);
                  } else {
                    // Fallback: fetch from regular optimize endpoint
                    api.post(`/analyze/optimize/${runId}`)
                      .then(r => resolve(r.data))
                      .catch(e => reject(new Error(e.response?.data?.detail || 'Optimization failed')));
                  }
                }
              }
            } catch { /* ignore individual poll errors */ }
          }, 600);
        })
        .catch(err => {
          stopPolling();
          if (!settled) {
            settled = true;
            reject(err);
          }
        });
    });
  },

  /** POST /api/analyze/judge/:id */
  async judgeIntent(runId) {
    const response = await api.post(`/analyze/judge/${runId}`);
    return response.data;
  },
};

export const historyService = {
  async getAll(limit = 50, offset = 0, filters = {}) {
    const params = { limit, offset };
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.scoreMin !== undefined) params.score_min = filters.scoreMin;
    if (filters.scoreMax !== undefined) params.score_max = filters.scoreMax;
    if (filters.domain) params.domain = filters.domain;
    const response = await api.get('/history', { params });
    return response.data;
  },

  async getOne(analysisId) {
    const response = await api.get(`/history/${analysisId}`);
    return response.data;
  },

  async delete(analysisId) {
    const response = await api.delete(`/history/${analysisId}`);
    return response.data;
  }
};

export const casesService = {
  async getAll() {
    const response = await api.get('/cases');
    return response.data;
  },

  async getSamples() {
    const response = await api.get('/cases/samples');
    return response.data;
  },

  async getOne(caseId) {
    const response = await api.get(`/cases/${caseId}`);
    return response.data;
  },

  async runCase(caseId) {
    const response = await api.post(`/cases/${caseId}/run`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/cases', data);
    return response.data;
  },

  async update(caseId, data) {
    const response = await api.put(`/cases/${caseId}`, data);
    return response.data;
  },

  async delete(caseId) {
    const response = await api.delete(`/cases/${caseId}`);
    return response.data;
  }
};

export const reportsService = {
  async get() {
    const response = await api.get('/reports');
    return response.data;
  },

  async getRunReport(runId) {
    const response = await api.get(`/reports/${runId}`);
    return response.data;
  },

  async getRunJsonExport(runId) {
    const response = await api.get(`/reports/${runId}/json`);
    return response.data;
  },
};

export const settingsService = {
  async get() {
    const response = await api.get('/settings');
    return response.data;
  },

  async update(data) {
    const response = await api.put('/settings', data);
    return response.data;
  },

  async export(data) {
    const response = await api.post('/settings/export', data);
    return response.data;
  },

  async deleteData(data) {
    const response = await api.post('/settings/delete-data', data);
    return response.data;
  }
};

export const adminService = {
  async getUsers(limit = 50, offset = 0) {
    const response = await api.get('/admin/users', { params: { limit, offset } });
    return response.data;
  },

  async updateUserRole(userId, role) {
    const response = await api.put(`/admin/users/${userId}/role`, { role });
    return response.data;
  },

  async getTrace(limit = 100) {
    const response = await api.get('/admin/trace', { params: { limit } });
    return response.data;
  },

  /** GET /api/admin/trace/:runId — full trace for a run */
  async getTraceDetail(runId) {
    const response = await api.get(`/admin/trace/${runId}`);
    return response.data;
  },

  async getConfig() {
    const response = await api.get('/admin/config');
    return response.data;
  },

  async updateModelConfig(data) {
    const response = await api.put('/admin/config/model', data);
    return response.data;
  },

  /** GET /api/admin/prompts — list all prompt versions */
  async getPrompts(role = null) {
    const params = role ? { role } : {};
    const response = await api.get('/admin/prompts', { params });
    return response.data;
  },

  /** GET /api/admin/prompts/:role/active */
  async getActivePrompt(role) {
    const response = await api.get(`/admin/prompts/${role}/active`);
    return response.data;
  },

  /** GET /api/admin/prompts/:role/versions */
  async getPromptVersions(role) {
    const response = await api.get(`/admin/prompts/${role}/versions`);
    return response.data;
  },

  /** POST /api/admin/prompts — create new prompt version */
  async createPromptVersion(data) {
    const response = await api.post('/admin/prompts', data);
    return response.data;
  },

  /** PUT /api/admin/prompts/:role/activate */
  async activatePromptVersion(role, version) {
    const response = await api.put(`/admin/prompts/${role}/activate`, { version });
    return response.data;
  },

  /** PUT /api/admin/prompts/:role/deactivate */
  async deactivatePromptVersion(role, version) {
    const response = await api.put(`/admin/prompts/${role}/deactivate`, { version });
    return response.data;
  },

  /** POST /api/admin/prompts/:role/rollback */
  async rollbackPrompt(role) {
    const response = await api.post(`/admin/prompts/${role}/rollback`);
    return response.data;
  },

  /** POST /api/admin/prompts/reload */
  async reloadPrompts() {
    const response = await api.post('/admin/prompts/reload');
    return response.data;
  },

  /** POST /api/admin/rubrics — create new rubric version */
  async createRubricVersion(data) {
    const response = await api.post('/admin/rubrics', data);
    return response.data;
  },

  /** POST /api/admin/models — update model config for a role */
  async updateModel(data) {
    const response = await api.post('/admin/models', data);
    return response.data;
  },
};

export const configService = {
  async getModels() {
    const response = await api.get('/config/models');
    return response.data;
  },

  async getRubric() {
    const response = await api.get('/config/rubric');
    return response.data;
  }
};

export const demoService = {
  async getStatus() {
    const response = await api.get('/demo/status');
    return response.data;
  },

  async toggle() {
    const response = await api.put('/demo/toggle');
    return response.data;
  },

  async getRuns() {
    const response = await api.get('/demo/runs');
    return response.data;
  },

  async getRunDetail(runId) {
    const response = await api.get(`/demo/runs/${runId}`);
    return response.data;
  },
};
